import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'
import { RowNode, ICellEditorParams } from 'ag-grid-community'

import Field from 'stores/field/Field'
import Record from 'stores/record/Record'

import StringInput from './StringInput'
import StringListInput from './StringListInput'
import NumberInput from './NumberInput'
import NumberListInput from './NumberListInput'
import BigIntInput from './BigIntInput'
import BigIntListInput from './BigIntListInput'
import BooleanInput from './BooleanInput'
import BooleanListInput from './BooleanListInput'
import DateTimeInput from './DateTimeInput'
import JsonInput from './JsonInput'
import JsonListInput from './JsonListInput'
import BytesInput from './BytesInput'
import BytesListInput from './BytesListInput'
import EnumInput from './EnumInput'
import EnumListInput from './EnumListInput'
import RelationInput from './RelationInput'
import RelationListInput from './RelationListInput'

import styles from './tableCellEditor.scss'

export interface IProps extends Omit<ICellEditorParams, 'value'> {
  node: ICellEditorParams['node'] & {
    data: Record
  }
  value: any

  field: Field
  getTableDimensions: () => {
    top: number
    bottom: number
    left: number
    right: number
    width: number
    height: number
  }
  resizeRowForRelation: (node: RowNode, field: Field) => void
}

class TableCellEditor extends React.Component<IProps> {
  private container: React.RefObject<HTMLDivElement> = React.createRef()
  private input: React.RefObject<any> = React.createRef()
  private initialValue: any
  private stopEditingImmediately: boolean

  constructor(props: IProps) {
    super(props)

    const {
      node: { data },
      field,
    } = props
    const record: Record = data

    this.stopEditingImmediately = false

    // props.keyPress is the key that started this edit

    if (props.keyPress === 8 || props.keyPress === 127) {
      // If `Backspace` or `Delete` key started this edit, clear the cell
      this.initialValue = field.lowestValidValue
      this.stopEditingImmediately = true
    } else if (props.charPress) {
      // If a valid character key started this edit, replace existing content with it
      if (props.field.isScalar && !props.field.isList) {
        // But only if the field is a non-list scalar
        this.initialValue = props.charPress
      }
    } else {
      this.initialValue = record.value[field.name]
    }
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  }

  // ag-grid takes over rendering, so it provides this in place of componentDidMount
  afterGuiAttached = () => {
    const { api, node, field, resizeRowForRelation } = this.props

    if (this.stopEditingImmediately) {
      api?.stopEditing()
      return
    }

    resizeRowForRelation(node, field)

    this.input.current?.afterGuiAttached?.()
    this.input.current?.focus()
  }

  // Called by ag-grid to get the final value when editing is finished
  getValue = () => this.input.current?.getValue?.()

  // Called by ag-grid to figure out if the editor should be bounded by the cell or not
  // We're always returning true because we'd like to break out of the cell boundaries to display a message if the cell's value is invalid
  isPopup = () => true

  handleClickOutside = (event: MouseEvent) => {
    if (!this.container.current?.contains(event.target as any)) {
      this.props.api?.stopEditing()
    }
  }

  render() {
    const {
      node: { data },
      columnApi,
      column,
      field,
    } = this.props
    const record: Record = data

    const width =
      columnApi!.getColumnState().find(s => s.colId === column.getColId())
        ?.width ?? 200

    const isInvalid = record.invalidFields.find(f => f.field.id === field.id)

    return (
      <div
        ref={this.container}
        className={cn(styles.container, { [styles.invalid]: !!isInvalid })}
        style={{ width }}
      >
        {
          // Strings
          field.isString &&
            (field.isList ? (
              <StringListInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ) : (
              <StringInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ))
        }

        {
          // Ints & Floats
          (field.isInt || field.isFloat || field.isDecimal) &&
            (field.isList ? (
              <NumberListInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ) : (
              <NumberInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ))
        }
        {
          // BigInts
          field.isBigInt &&
            (field.isList ? (
              <BigIntListInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ) : (
              <BigIntInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ))
        }

        {
          // Booleans
          field.isBoolean &&
            (field.isList ? (
              <BooleanListInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ) : (
              <BooleanInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ))
        }

        {
          // DateTimes
          field.isDateTime &&
            (field.isList ? (
              <StringListInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ) : (
              <DateTimeInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ))
        }

        {
          // Json
          field.isJson &&
            (field.isList ? (
              <JsonListInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ) : (
              <JsonInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ))
        }

        {
          // Bytes
          field.isBytes &&
            (field.isList ? (
              <BytesListInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ) : (
              <BytesInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ))
        }

        {
          // Enums
          field.isEnum &&
            (field.isList ? (
              <EnumListInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ) : (
              <EnumInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ))
        }

        {
          // Relations
          field.isRelation &&
            (field.isList ? (
              <RelationListInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ) : (
              <RelationInput
                ref={this.input}
                {...this.props}
                initialValue={this.initialValue}
              />
            ))
        }

        {isInvalid && <div className={styles.phantom}>{isInvalid.reason}</div>}
      </div>
    )
  }
}

export default observer(TableCellEditor)
